// Analytics drill-down <-> registrations page URL <-> registrations filter map (backend keys).
export const FILTER_PARAM_KEYS = ['status', 'source', 'checkin', 'notcheckin', 'field', 'value'];
// Repeatable param holding extra AND-ed conditions, e.g. and=field:Category:Delegate&and=checkin:lunch_1
export const CONDITION_PARAM = 'and';

export const STATUS_LABELS = {
  REGISTERED: 'Registered',
  PENDING_PAYMENT: 'Pending payment',
  PAID: 'Paid',
  PENDING_FOR_APPROVAL: 'Pending approval',
  PAYMENT_FAILED: 'Payment failed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
};

export function drillDownSearch(target) {
  const params = new URLSearchParams();
  if (target.type === 'status') params.set('status', target.value);
  if (target.type === 'source') params.set('source', target.value);
  if (target.type === 'checkin') params.set('checkin', target.value);
  if (target.type === 'field') {
    params.set('field', target.field);
    params.set('value', target.value);
  }
  return params.toString();
}

/** Backend filter key a condition writes; conditions sharing a key replace each other. */
export function conditionKey(target) {
  if (target.type === 'checkin' || target.type === 'notcheckin') return `checkins.${target.value}`;
  if (target.type === 'field') return `formData.${target.field}`;
  if (target.type === 'invite') return 'inviteLinkId';
  return target.type;
}

export function conditionLabel(target, purposeNames = {}) {
  const purpose = purposeNames[target.value] || target.value;
  if (target.type === 'status') return `Status: ${STATUS_LABELS[target.value] || target.value}`;
  if (target.type === 'source') return `Source: ${target.value}`;
  if (target.type === 'checkin') return `Checked in: ${purpose}`;
  if (target.type === 'notcheckin') return `Not checked in: ${purpose}`;
  if (target.type === 'invite') return `Invite: ${target.name || 'link'}`;
  return `${target.field}: ${target.value}`;
}

const conditionValue = (target) => {
  if (target.type === 'checkin') return 'true';
  if (target.type === 'notcheckin') return 'false';
  return target.value;
};

export function encodeCondition(target) {
  const parts = target.type === 'field' ? [target.type, target.field, target.value]
    : target.type === 'invite' ? [target.type, target.value, target.name || ''] : [target.type, target.value];
  return parts.map((part, index) => (index === 0 ? part : encodeURIComponent(part))).join(':');
}

export function decodeCondition(raw) {
  const [type, ...rest] = String(raw || '').split(':');
  try {
    const parts = rest.map(decodeURIComponent);
    if (type === 'field' && parts.length === 2 && parts[0] && parts[1]) return { type, field: parts[0], value: parts[1] };
    if (type === 'invite' && parts[0]) return { type, value: parts[0], name: parts[1] || '' };
    if (['status', 'source', 'checkin', 'notcheckin'].includes(type) && parts.length === 1 && parts[0]) return { type, value: parts[0] };
  } catch (_) { /* malformed param */ }
  return null;
}

/** Search string carrying all targets as AND-ed conditions. */
export function conditionsSearch(targets) {
  const params = new URLSearchParams();
  targets.forEach((target) => params.append(CONDITION_PARAM, encodeCondition(target)));
  return params.toString();
}

/** Single-value filter-bar params as condition targets. */
function legacyTargets(params) {
  const targets = [];
  ['status', 'source', 'checkin'].forEach((type) => {
    const value = params.get(type);
    if (value) targets.push({ type, value, param: type });
  });
  const notCheckin = params.get('notcheckin');
  if (notCheckin && notCheckin !== params.get('checkin')) targets.push({ type: 'notcheckin', value: notCheckin, param: 'notcheckin' });
  const field = params.get('field');
  const value = params.get('value');
  if (field && value) targets.push({ type: 'field', field, value, param: 'field' });
  return targets;
}

/**
 * Returns { filters, labels, conditions } for a URL search string. All conditions are AND-ed; the
 * filter-bar params are applied after the repeatable "and" params, so they win on the same key.
 */
export function filtersFromSearch(search, purposeNames) {
  const params = new URLSearchParams(search || '');
  const byKey = new Map();
  const add = (target) => {
    const key = conditionKey(target);
    byKey.delete(key);
    byKey.set(key, target);
  };
  params.getAll(CONDITION_PARAM).forEach((raw) => {
    const target = decodeCondition(raw);
    if (target) add({ ...target, raw });
  });
  legacyTargets(params).forEach(add);

  const filters = {};
  const conditions = [];
  byKey.forEach((target, key) => {
    filters[key] = conditionValue(target);
    conditions.push({ ...target, key, label: conditionLabel(target, purposeNames) });
  });
  return { filters, labels: conditions.map((c) => c.label), conditions };
}

/** Removes one condition (as returned by filtersFromSearch), keeping everything else. */
export function searchWithoutCondition(search, condition) {
  const params = new URLSearchParams(search || '');
  if (condition.param) {
    params.delete(condition.param);
    if (condition.param === 'field') params.delete('value');
  }
  const remaining = params.getAll(CONDITION_PARAM)
    .filter((raw) => {
      const target = decodeCondition(raw);
      return target && conditionKey(target) !== condition.key;
    });
  params.delete(CONDITION_PARAM);
  remaining.forEach((raw) => params.append(CONDITION_PARAM, raw));
  return params.toString();
}

export function searchWithoutFilterParams(search) {
  const params = new URLSearchParams(search || '');
  [...FILTER_PARAM_KEYS, CONDITION_PARAM].forEach((key) => params.delete(key));
  return params.toString();
}

/** Current filter selections as plain params ({ status, source, checkin, notcheckin, field, value }). */
export function filterParamsFromSearch(search) {
  const params = new URLSearchParams(search || '');
  return FILTER_PARAM_KEYS.reduce((acc, key) => ({ ...acc, [key]: params.get(key) || '' }), {});
}

/** Applies filter selections onto a search string, keeping unrelated params; blank values are removed. */
export function searchWithFilterParams(search, selections) {
  const params = new URLSearchParams(search || '');
  FILTER_PARAM_KEYS.forEach((key) => {
    const value = selections[key];
    if (value) params.set(key, value); else params.delete(key);
  });
  if (!params.get('field')) {
    params.delete('value');
  }
  return params.toString();
}
